import { createContext, useContext } from "react";

import {
  CardFlag,
  ChainCard,
  ChainMaker,
  CoverageFlag,
  CoverageRow,
  FlagRule,
  RuleFlag,
  ValueChain,
} from "../../types/valueChain";
import { ChainLens } from "./valueChainState";

// What the cards of the canvas draw besides their makers: the lens, and
// under the jurisdiction lens the home jurisdiction, the trusted set and the
// flags of the response's coverage rows. A lens restyles a card; it never
// adds or removes one.
export interface ChainLensView {
  lens: ChainLens;
  // The home jurisdiction the read split the makers by; null when none
  home: string | null;
  // The countries the read trusted beside it, in capitals: their own bucket
  trusted: readonly string[];
  // The flags of each card's coverage row, by the card's id
  flags: ReadonlyMap<string, readonly CoverageFlag[]>;
}

export const ChainLensContext = createContext<ChainLensView>({
  lens: "makers",
  home: null,
  trusted: [],
  flags: new Map(),
});

export const useChainLens = (): ChainLensView => useContext(ChainLensContext);

// The flag of a card with exactly one maker on record
export const SINGLE_MAKER: CardFlag = "single_maker";

// The words of each rule flag, as the card's chip and the report say them
export const FLAG_WORDS: Record<RuleFlag, string> = {
  no_domestic_maker: "no domestic maker on record",
  fewer_than_two_domestic: "fewer than two domestic makers on record",
  no_domestic_or_trusted_maker: "no domestic or trusted maker on record",
  fewer_than_two_domestic_or_trusted:
    "fewer than two domestic or trusted makers on record",
};

// Whether a flag is the rule's, not the card's own
export const isRuleFlag = (flag: CoverageFlag): flag is RuleFlag =>
  flag !== SINGLE_MAKER;

// The words of the single-maker flag, with the country of that one maker
export const singleMakerWords = (country: string | null): string =>
  `single maker on record · ${country ?? "country unknown"}`;

// The words of any flag of a coverage row
export const flagWords = (
  flag: CoverageFlag,
  singleMakerCountry: string | null,
): string =>
  isRuleFlag(flag) ? FLAG_WORDS[flag] : singleMakerWords(singleMakerCountry);

// The coverage rows of every read the page holds, each card once: the rows
// of the first read that has one for it, the root read first. The numbers
// are the responses'.
export const coverageRows = (
  reads: readonly (ValueChain | null | undefined)[],
): CoverageRow[] => {
  const seen = new Set<string>();
  const rows: CoverageRow[] = [];
  for (const read of reads) {
    for (const row of read?.coverage ?? []) {
      if (!seen.has(row.card)) {
        seen.add(row.card);
        rows.push(row);
      }
    }
  }
  return rows;
};

// The flags of every card of the reads, by the card's id, as their coverage
// rows carry them. The first read that has a row for a card wins.
export const coverageFlags = (
  reads: readonly (ValueChain | null | undefined)[],
): Map<string, readonly CoverageFlag[]> => {
  const flags = new Map<string, readonly CoverageFlag[]>();
  for (const row of coverageRows(reads)) {
    flags.set(row.card, row.flags);
  }
  return flags;
};

// The word of the bucket of makers with no country on record
export const UNKNOWN_COUNTRY = "unknown";

// The browser's names of regions, in English; null where it has none
type RegionNames = { of: (code: string) => string | undefined };
const regionNames: RegionNames | null = (() => {
  const DisplayNames = (
    Intl as unknown as {
      DisplayNames?: new (
        locales: string[],
        options: { type: "region" },
      ) => RegionNames;
    }
  ).DisplayNames;
  try {
    return DisplayNames ? new DisplayNames(["en"], { type: "region" }) : null;
  } catch {
    return null;
  }
})();

// The name of a country by its code, where the browser knows it: "Norway"
// for NO; the code itself otherwise
export const countryName = (code: string): string => {
  const upper = code.toUpperCase();
  try {
    const name = regionNames?.of(upper);
    return name && name !== upper ? name : upper;
  } catch {
    return upper;
  }
};

// The rule's flag as the rule and the trusted set name it
export const ruleFlagOf = (rule: FlagRule, trusted: boolean): RuleFlag =>
  rule === "none_domestic"
    ? trusted
      ? "no_domestic_or_trusted_maker"
      : "no_domestic_maker"
    : trusted
      ? "fewer_than_two_domestic_or_trusted"
      : "fewer_than_two_domestic";

// Whether a maker is domestic: at home in the home jurisdiction itself. A
// maker at home in a trusted country is trusted, never domestic.
export const isDomestic = (maker: ChainMaker, home: string | null): boolean =>
  home !== null && (maker.country ?? "").toUpperCase() === home;

// Every country code the makers of the reads are at home in, sorted
export const chainCountries = (
  reads: readonly (ValueChain | null | undefined)[],
): string[] => {
  const codes = new Set<string>();
  for (const read of reads) {
    if (!read) {
      continue;
    }
    const cards: ChainCard[] = [read.root, ...read.cards];
    for (const card of cards) {
      for (const entry of card.countries ?? []) {
        if (entry.country) {
          codes.add(entry.country.toUpperCase());
        }
      }
    }
  }
  return Array.from(codes).sort();
};
